/**
 * CurrencyRiskAnalysis - FX Impact Scenarios section
 * Extracted from ConsolidatedHoldings.tsx (TASK-371 decomposition)
 *
 * Computes FX risk scenarios for foreign currency exposures
 * and renders impact projections with hedging status.
 */

import React from 'react';
import { DollarSign } from 'lucide-react';

export interface CurrencyRiskAnalysisProps {
  currencyData: Array<{ name: string; value: number }>;
  totalValue: number;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `CHF ${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `CHF ${(value / 1000).toFixed(0)}K`;
  return `CHF ${value.toLocaleString('de-CH')}`;
};

const CurrencyRiskAnalysis: React.FC<CurrencyRiskAnalysisProps> = ({
  currencyData,
  totalValue,
}) => {
  if (currencyData.length <= 1) return null;

  // Calculate FX risk for major foreign currencies
  const fxScenarios = currencyData
    .filter(c => c.name !== 'CHF')
    .map(c => {
      const exposure = totalValue * (c.value / 100);
      const impactDrop10 = exposure * 0.10; // 10% FX drop scenario
      return {
        currency: c.name,
        exposure: exposure,
        percentage: c.value,
        impactDrop10: impactDrop10
      };
    })
    .filter(s => s.percentage > 5); // Only show currencies >5%

  if (fxScenarios.length === 0) return null;

  const totalFXImpact = fxScenarios.reduce((sum, s) => sum + s.impactDrop10, 0);
  const currentHedge = 0; // Mock: No hedge currently

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
          <DollarSign className="w-5 h-5 text-orange-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Currency Risk Analysis</h3>
      </div>

      <div className="space-y-4">
        {/* FX Exposure Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-2">Total Foreign Currency Exposure</div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(fxScenarios.reduce((sum, s) => sum + s.exposure, 0))}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {fxScenarios.reduce((sum, s) => sum + s.percentage, 0).toFixed(1)}% of total portfolio
          </div>
        </div>

        {/* Impact Scenarios */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">FX Impact Scenarios</h4>
          <div className="space-y-2">
            {fxScenarios.map((scenario, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-200">
                <div>
                  <span className="font-medium text-gray-900">{scenario.currency}</span>
                  <span className="text-sm text-gray-600 ml-2">
                    ({formatCurrency(scenario.exposure)})
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-red-600">
                    -{formatCurrency(scenario.impactDrop10)}
                  </div>
                  <div className="text-xs text-gray-500">if drops 10%</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hedging Status */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-semibold text-blue-900">Hedge Status</div>
              <div className="text-sm text-blue-700 mt-1">
                Currently {currentHedge === 0 ? 'unhedged' : `${currentHedge}% hedged`}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-blue-900">
                {currentHedge}%
              </div>
              <div className="text-xs text-blue-700">hedged</div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-blue-200">
            <div className="text-sm text-blue-800">
              <strong>Worst-case scenario:</strong> If all foreign currencies drop 10%, total loss = {formatCurrency(totalFXImpact)}
            </div>
            <div className="text-xs text-blue-700 mt-2">
              Consider hedging {((fxScenarios.reduce((sum, s) => sum + s.percentage, 0) * 0.5).toFixed(0))}% of FX exposure (cost: ~1% annually)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrencyRiskAnalysis;
